<template lang="pug">
    div
        Button(shape="circle" icon="md-help" size="small"  @click="instance(true)" )
    </template>
<script>
export default {
    name: "ExplainCacheUrlRule",
    props: {},
    data() {
        return {
            "en-US": {
                title: "Supplementary information",
                content: `
                    <p>Advanced cache can determine whether a request matches a cached file based on individual request Args (i.e., query string, such as v=1). By default, we treat requests with different args as requests for different resources. When setting up rules, we can adopt the following strategies:</p>
                    <p>Keep: The caching system "will" differentiate resources based on these request Args.</p>
                    <p>Ignore: The caching system "will not" differentiate resources based on these request Args.</p>
                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>Keep All</li></h3>
                            <p style="margin-left: 20px;">
                                The cache system creates cache files based on both 
                                <strong>the request Path</strong>
                                and 
                                <strong>the request Args</strong>
                                .This means that even if the request Paths are identical, but the request Args differ, they will be treated as separate resources and cached individually.
                            </p>
                            <div style="margin-left: 20px;">
                                <p>Example:</p>
                                <p style="margin-left: 20px;">A cache file named "a.css?region=us&v=1" will be created for the request "a.css?region=us&v=1".
                                <p style="margin-left: 20px;">A separate cache file, "a.css?region=jp&v=1", will be created for the request "a.css?region=jp&v=1".</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>Ignore All</li></h3>
                            <p style="margin-left: 20px;">The caching system creates cache files based on 
                                <strong>the request Path</strong>
                                .This means that even if the request Args differ, requests with the same path will be considered as requests for the same resource.
                            </p>
                            <div style="margin-left: 20px;">
                                <p>Example:</p>
                                <p style="margin-left: 20px;">A cache file for "a.css" will be created when requesting "a.css?v=1".</p>
                                <p style="margin-left: 20px;">When requesting "a.css?v=2", the node will ignore the difference in request Args and directly return the cached file for "a.css" since it already exists in the cache.</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>Keep Params / Ignore Params</li></h3>
                            <p style="margin-left: 20px;">
                                You can achieve more flexible configurations by 
                                <strong>Keep Params</strong>
                                or 
                                <strong>Ignore Params</strong>
                                .
                            </p>
                            <div style="margin-left: 20px;">
                                <p>Example:</p>
                                <p style="margin-left: 20px;">
                                    If you set 
                                    <strong>Ignore Params</strong>
                                    to "region="
                                </p>
                                <p style="margin-left: 40px;">Request (1) "a.css?region=us&v=1" will create cache file (1) "a.css?v=1".</p>
                                <p style="margin-left: 40px;">When request (2) is "a.css?region=jp&v=1", since the cache system already has a cached file for "a.css?v=1", the node will ignore the difference in the request Args "region=jp" and directly return the cached file (1).</p>
                                <p style="margin-left: 20px;">
                                    If you set 
                                    <strong>Keep Params</strong>
                                    to "v="
                                </p>
                                <p style="margin-left: 40px;">Request (1) "a.css?region=us&v=1" will create cache file (1) "a.css?v=1".</p>
                                <p style="margin-left: 40px;">When request (2) is "a.css?region=jp&v=1", since the cache system already has a cached file for "a.css?v=1", the node will only retain the "v=1" args and directly return the cached file (1).</p>
                                <p style="margin-left: 40px;">When request (3) is "a.css?region=jp&v=2", and since the cache system does not have a cached file for "a.css?v=2", a new cache file (3) "a.css?v=2" will be created.</p>
                                <p style="margin-left: 20px;">
                                    In example (1) and (2), both
                                    <strong>Ignore Params</strong>
                                    "region=" or
                                    <strong>Keep Params</strong>
                                     "v=" can achieve the same caching effect. The specific configuration can be chosen based on your scenario requirements.
                                </p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>When an advanced caching rule is matched, its specified caching or no-caching behavior will be followed with priority. If no advanced rule is matched, the general caching and no-caching rules will be applied sequentially.</li></h3>
                        </div>
                    </ul>
                `,
            },
            "zh-CN": {
                title: "说明补充",
                content: `
                    <p>进阶缓存可针对个别的请求 Args（即 Query String，如 v=1），决定请求是否匹配缓存档案。默认的情况下，我们会将相异的请求 Args，视为请求不同的资源。在设置规则时，我们可采取以下策略：</p>
                    <p>保留：缓存系统「会」根据此请求 Args 区分资源。</p>
                    <p>忽略：缓存系统「不会」根据此请求 Args 区分资源。</p>
                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>全部保留</li></h3>
                            <p style="margin-left: 20px;">
                                缓存系统会根据每个请求的
                                <strong>路径</strong>
                                与
                                <strong>请求 Args</strong>
                                ，建立缓存文件。这意味着即使请求路径相同，只要请求 Args 不同，就会被视为不同的资源，并分别缓存。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">a.css?region=us&v=1，会建立缓存文件 a.css?region=us&v=1。
                                <p style="margin-left: 20px;">a.css?region=jp&v=1，则会另外建立缓存文件 a.css?region=jp&v=1。</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>全部忽略</li></h3>
                            <p style="margin-left: 20px;">缓存系统会根据
                                <strong>请求路径</strong>
                                ，建立缓存文件。这意味着请求路径相同，但请求 Args 不同的情况下，仍会被视为请求相同的资源。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">a.css?v=1，会建立缓存 a.css 的缓存文件。</p>
                                <p style="margin-left: 20px;">a.css?v=2 时，因缓存系统已有路径为 a.css 的缓存文件，节点会忽略请求 Args 的差异，直接返还 a.css 的缓存文件。</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>保留指定参数 / 忽略指定参数</li></h3>
                            <p style="margin-left: 20px;">
                                您也可以通过
                                <strong>保留指定参数</strong>
                                或
                                <strong>忽略指定参数</strong>
                                来实现更灵活的配置。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">
                                    若您设置
                                    <strong>忽略指定参数</strong>
                                    为 region= 
                                </p>
                                <p style="margin-left: 40px;">请求(1) 是 a.css?region=us&v=1，会建立缓存(1) a.css?v=1 的缓存文件。</p>
                                <p style="margin-left: 40px;">当请求(2) 是a.css?region=jp&v=1 时，因缓存系统已有a.css?v=1 的缓存文件，节点会忽略请求Args region=jp 的差异，直接返还缓存文件(1 )。</p>
                                <p style="margin-left: 20px;">
                                    若您设置
                                    <strong>保留指定参数</strong>
                                    為 v = 
                                </p>
                                <p style="margin-left: 40px;">請求(1) 是 a.css?region=us&v=1，會建立緩存(1) a.css?v=1 的緩存文件。</p>
                                <p style="margin-left: 40px;">当请求(2) 是 a.css?region=jp&v=1 时，因缓存系统已有 a.css?v=1 的缓存文件，节点只保留 Args v=1，直接返还缓存文件(1)。</p>
                                <p style="margin-left: 40px;">当请求(3) 是 a.css?region=jp&v=2 时，因缓存系统没有a.css?v=2 的缓存文件，则会建立缓存(3) a.css?v=2 的缓存文件。</p>
                                <p style="margin-left: 20px;">
                                    以例子(1)(2)为例，
                                    <strong>忽略指定参数</strong>
                                    region=  或
                                    <strong>保留指定参数</strong>
                                    v=  ，都能实现相同的缓存效果，具体配置可根据您的场景需求进行选择。
                                </p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>进阶缓存规则命中时，将优先遵循其设定的缓存或不缓存规则。若未命中进阶规则，则依序套用一般缓存与不缓存规则。</li></h3>
                        </div>
                    </ul>
                `,
            },
            "zh-TW": {
                title: "說明補充",
                content: `
                    <p>進階緩存可針對個別的請求 Args（即 Query String，如 v=1），決定請求是否匹配緩存檔案。默認的情況下，我們會將相異的請求 Args，視為請求不同的資源。在設置規則時，我們可採取以下策略：</p>
                    <p>保留：緩存系統「會」根據此請求 Args 區分資源。</p>
                    <p>忽略：緩存系統「不會」根據此請求 Args 區分資源。</p>
                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>全部保留</li></h3>
                            <p style="margin-left: 20px;">
                                緩存系統會根據每個請求的
                                <strong>路徑</strong>
                                與
                                <strong>請求 Args</strong>
                                ，建立緩存文件。這意味著即使請求路徑相同，只要請求 Args 不同，就會被視為不同的資源，並分別緩存。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">a.css?region=us&v=1，會建立緩存文件 a.css?region=us&v=1。
                                <p style="margin-left: 20px;">a.css?region=jp&v=1，則會另外建立緩存文件 a.css?region=jp&v=1。</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>全部忽略</li></h3>
                            <p style="margin-left: 20px;">緩存系統會根據
                                <strong>請求路徑</strong>
                                ，建立緩存文件。這意味著請求路徑相同，但請求 Args 不同的情況下，仍會被視為請求相同的資源。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">a.css?v=1，會建立緩存 a.css 的緩存文件。</p>
                                <p style="margin-left: 20px;">a.css?v=2 時，因緩存系統已有路徑為 a.css 的緩存文件，節點會忽略請求 Args 的差異，直接返還 a.css 的緩存文件。</p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>保留指定參數 / 忽略指定參數</li></h3>
                            <p style="margin-left: 20px;">
                                您也可以通過
                                <strong>保留指定參數</strong>
                                或
                                <strong>忽略指定參數</strong>
                                來實現更靈活的配置。
                            </p>
                            <div style="margin-left: 20px;">
                                <p>例如：</p>
                                <p style="margin-left: 20px;">
                                    若您設置
                                    <strong>忽略指定參數</strong>
                                    為 region= 
                                </p>
                                <p style="margin-left: 40px;">請求(1) 是 a.css?region=us&v=1，會建立緩存(1) a.css?v=1 的緩存文件。</p>
                                <p style="margin-left: 40px;">當請求(2) 是 a.css?region=jp&v=1 時，因緩存系統已有 a.css?v=1 的緩存文件，節點會忽略請求 Args region=jp 的差異，直接返還緩存文件(1)。</p>
                                <p style="margin-left: 20px;">
                                    若您設置
                                    <strong>保留指定參數</strong>
                                    為 v = 
                                </p>
                                <p style="margin-left: 40px;">請求(1) 是 a.css?region=us&v=1，會建立緩存(1) a.css?v=1 的緩存文件。</p>
                                <p style="margin-left: 40px;">當請求(2) 是 a.css?region=jp&v=1 時，因緩存系統已有 a.css?v=1 的緩存文件，節點只保留 Args v=1，直接返還緩存文件(1)。</p>
                                <p style="margin-left: 40px;">當請求(3) 是 a.css?region=jp&v=2 時，因緩存系統沒有a.css?v=2 的緩存文件，則會建立緩存(3) a.css?v=2 的緩存文件。</p>
                                <p style="margin-left: 20px;">
                                    以例子(1)(2)為例，
                                    <strong>忽略指定參數</strong>
                                    region=  或
                                    <strong>保留指定參數</strong>
                                    v=  ，都能實現相同的緩存效果，具體配置可根據您的場景需求進行選擇。
                                </p>
                            </div>
                        </div>
                    </ul>

                    <hr style="margin-top: 5px;margin-bottom: 5px;">

                    <ul style="margin-left: 20px;">
                        <div>
                            <h3><li>進階緩存規則命中時，將優先遵循其設定的緩存或不緩存規則。若未命中進階規則，則依序套用一般緩存與不緩存規則。</li></h3>
                        </div>
                    </ul>
                `,
            },
        };
    },
    methods: {
        instance(scrollable) {
            this.$Modal.info({
                width: "800px",
                title: this[this.$i18n.locale].title,
                content: this[this.$i18n.locale].content,
                okText: this.$i18n.t("rcmtable.confirmText"),
                // scrollable: scrollable,
            });
        },
    },
    computed: {},
};
</script>
<style></style>



// WEBPACK FOOTER //
// src/components/PoptipInfos/ExplainCacheUrlRule.vue